import type {
  ChatMessage,
  ChatTranslations
} from "@genexus/chameleon-controls-library";

import { MercuryChatMessageMetadata } from "@genexus/mercury";

export const ASSISTANT_RESPONSE_SHORT_MARKDOWN =
  '\n### Code block {#code-block}\nTo create code blocks, you’ll use three backticks (` ``` `) or three tildes (`~~~`) on the lines before and after the code block.\n\n```json\n{\n  "firstName": "John",\n  "lastName": "Smith",\n  "age": 25\n}';

export const ASSISTANT_NAME = "Nexa";

// - - - - - - - - - - - - - -
// Types of messages
// - - - - - - - - - - - - - -

export const userMessageChatModel: ChatMessage[] = [
  {
    id: "1",
    role: "user",
    content:
      "Generate a web form for customer registration with fields for name, email, phone, and address. Include validation, save the data to the database, and show a confirmation message after submission.",
    metadata: null
  }
];

export const assistantMessageChatModel: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    status: "complete",
    content:
      "I’ve created the basic structure for the registration form, but I need a bit more info: Should the address include city, state, and ZIP? Do you want a separate confirmation page or just a message on the same screen? Any specific format or validation for the phone number?",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  }
];

export const assistantMessageWarningChatModel: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    status: "complete",
    content:
      "The CustomerRegistration panel was created with warnings: The “Phone” field uses a generic character format—consider a phone mask. No address validation rules applied.The data is still saved correctly, and the panel works as expected.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata,
    parts: "warning"
  }
];

export const assistantMessageSuccessChatModel: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    status: "complete",
    content:
      "The CustomerRegistration web panel was created successfully. Fields: Name, Email, Phone, Address. Validations applied (required, email format). Data saved to “Customer” transaction. Confirmation message shown after submit. Let me know if you'd like to add styles or link it to another object.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata,
    parts: "success"
  }
];

export const errorMessageChatModel: ChatMessage[] = [
  {
    id: "1",
    role: "error",
    content:
      "Sorry, I couldn't generate the requested form. Reason: The “Customer” transaction does not exist in the current Knowledge Base. Please create the “Customer” object or check if it’s named differently.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  }
];

export const assistantWaitingChatModel: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    status: "waiting",
    content: "Creating CustomerRegistration panel",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  }
];

export const chatTranslations: ChatTranslations = {
  accessibleName: {
    clearChat: "Clear chat",
    copyMessageContent: "Copy assistant content",
    downloadCodeButton: "Download code",
    sendButton: "Send",
    sendInput: "Message",
    stopResponseButton: "Stop generating answer"
  },
  placeholder: {
    sendInput: "Ask me a question..."
  },
  text: {
    copyCodeButton: "Copy code",
    copyMessageContent: "Copy",
    downloadCodeButton: "Download",
    processing: `Processing...`,
    sourceFiles: "Source files:",
    stopResponseButton: "Stop generating answer"
  }
};

// All messages

export const allMessagesModel: ChatMessage[] = [
  {
    id: "1",
    role: "user",
    content:
      "Generate a web form for customer registration with fields for name, email, phone, and address. Include validation, save the data to the database, and show a confirmation message after submission.",
    metadata: null
  },
  {
    id: "1",
    role: "assistant",
    status: "complete",
    content:
      "I’ve created the basic structure for the registration form, but I need a bit more info: Should the address include city, state, and ZIP? Do you want a separate confirmation page or just a message on the same screen? Any specific format or validation for the phone number?",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  },
  {
    id: "1",
    role: "assistant",
    status: "complete",
    content:
      "The CustomerRegistration panel was created with warnings: The “Phone” field uses a generic character format—consider a phone mask. No address validation rules applied.The data is still saved correctly, and the panel works as expected.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata,
    parts: "warning"
  },
  {
    id: "1",
    role: "error",
    content:
      "Sorry, I couldn't generate the requested form. Reason: The “Customer” transaction does not exist in the current Knowledge Base. Please create the “Customer” object or check if it’s named differently.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  },
  {
    id: "5",
    role: "assistant",
    status: "complete",
    content:
      "Your information has been saved successfully, and all changes are now reflected in your account. You can continue with the next steps or return to the dashboard at any time.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "15:00"
    } satisfies MercuryChatMessageMetadata,
    parts: "success"
  }
];

// - - - - - - - - - - - - - -
// Other messages
// - - - - - - - - - - - - - -

export const ASSISTANT_RESPONSE_MARKDOWN = `
###### Code block {#code-block}
To create code blocks, you’ll use three backticks (\` \`\`\` \`) or three tildes (\`~~~\`) on the lines before and after the code block.

\`\`\`
{
  "firstName": "John",
  "lastName": "Smith",
  "age": 25
}
\`\`\`

Another way to create code blocks is to indent every line of the block by at least four spaces or one tab.

    <html>
      <head>
      </head>
    </html>


###### Syntax Highlighting {#syntax-highlighting}
This feature allows you to add color highlighting for whatever language your code was written in.
To add syntax highlighting, specify a language next to the backticks before the fenced code block.

\`\`\`json
{
  "firstName": "John",
  "lastName": "Smith",
  "age": 25
}
\`\`\`

\`\`\`javascript
import React from 'react'
import ReactDOM from 'react-dom'
import Markdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'

const markdown = \`
# Your markdown here
\`

ReactDOM.render(
  <Markdown rehypePlugins={[rehypeHighlight]}>{markdown}</Markdown>,
  document.querySelector('#content')
)
\`\`\`

###### Horizontal Rules {#horizontal-rules}
To create a horizontal rule, use three or more asterisks (\`***\`), dashes (\`---\`), or underscores (\`___\`) on a line by themselves.
`;

export const chatRecord: ChatMessage[] = [
  { id: "1", role: "user", content: "Hello world" },
  { id: "2", role: "assistant", content: ASSISTANT_RESPONSE_MARKDOWN },
  { id: "3", role: "user", content: "Hello world 1" },
  { id: "4", role: "assistant", content: ASSISTANT_RESPONSE_SHORT_MARKDOWN },
  { id: "5", role: "user", content: "Hello world 2" },
  { id: "6", role: "assistant", content: ASSISTANT_RESPONSE_SHORT_MARKDOWN },
  { id: "7", role: "user", content: "Hello world 3" },
  { id: "8", role: "assistant", content: ASSISTANT_RESPONSE_SHORT_MARKDOWN },
  { id: "9", role: "user", content: "Hello world 4" },
  { id: "10", role: "assistant", content: ASSISTANT_RESPONSE_SHORT_MARKDOWN }
];

export const longChatRecord: ChatMessage[] = Array.from(
  { length: 40 },
  (_, index) =>
    index % 2 === 0
      ? {
          id: `index: ${index}`,
          role: "user",
          content:
            `index: ${index}` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n`
        }
      : {
          id: `index: ${index}`,
          role: "assistant",
          content:
            ASSISTANT_RESPONSE_SHORT_MARKDOWN +
            `\nindex: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n` +
            `index: ${index}\n`
        }
);

export const codeFixerRecord: ChatMessage[] = [
  {
    id: "1",
    role: "user",
    content: "Please give me an example about...",
    metadata: "14:55"
  },
  {
    id: "2",
    role: "assistant",
    status: "complete",
    content:
      "Action required example. Action required example. Action required example.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  },
  {
    id: "3",
    role: "assistant",
    status: "complete",
    content:
      "Some fields contain missing or invalid data, which may prevent the process from completing correctly. Review your input carefully and make any necessary corrections before continuing.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata,
    parts: "warning"
  },
  {
    id: "4",
    role: "error",
    content:
      "An unexpected error occurred while processing your request, and the operation couldn’t be completed.Please check your internet connection or try again later. If the problem persists, contact support.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  },
  {
    id: "5",
    role: "assistant",
    status: "complete",
    content:
      "Your information has been saved successfully, and all changes are now reflected in your account. You can continue with the next steps or return to the dashboard at any time.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata,
    parts: "success"
  },
  {
    id: "6",
    role: "user",
    content:
      "Implement the function calculate_average_grade in grades.py that takes a list of grades as input and returns the average grade as a floating-point number",
    metadata: "15:00"
  },
  {
    id: "7",
    role: "assistant",
    status: "complete",
    content:
      "You can provide further details or updates regarding your support ticket and its associated code. Your input here helps us better understand and address your issue effectively.",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  },
  {
    id: "8",
    role: "assistant",
    status: "waiting",
    content: "You can provide further details or updates",
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  },
  {
    id: "9",
    role: "assistant",
    status: "complete",
    content: ASSISTANT_RESPONSE_SHORT_MARKDOWN,
    metadata: {
      agentName: ASSISTANT_NAME,
      time: "14:25"
    } satisfies MercuryChatMessageMetadata
  }
];
